import { createFileRoute } from "@tanstack/react-router";
import { marketingHead } from "@/lib/marketing/seo";
import { breadcrumbs, webPageOf } from "@/lib/marketing/ppl-schema";
import { CaseStudyGate, useCaseStudyAccess } from "@/components/marketing/proof/case-study-gate";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { MarketingCta } from "@/components/marketing/site/marketing-cta";
import { StudyHero } from "@/components/marketing/proof/case-study";
import { StudyBody } from "@/components/marketing/proof/study-body";
import { StudyFooter } from "@/components/marketing/proof/study-footer";
import { SarahStudySummary } from "@/routes/v2/-components/sarah-study-summary";
import { SarahStudyBackground } from "@/routes/v2/-components/sarah-study-background";
import { SarahStudyDeployed } from "@/routes/v2/-components/sarah-study-deployed";
import { SarahStudyMethod } from "@/routes/v2/-components/sarah-study-method";
import { SarahStudyResults } from "@/routes/v2/-components/sarah-study-results";
import { SarahStudyConclusions } from "@/routes/v2/-components/sarah-study-conclusions";
import { SarahStudyAppendix } from "@/routes/v2/-components/sarah-study-appendix";
import { SarahStudyReferences } from "@/routes/v2/-components/sarah-study-references";

/* Sarah case study v1.3 (8 September 2026). Figures come from the production call table read at
 * 2:38 pm Central on 7 September and change only when the study is re-issued. Working:
 * ~/Desktop/PEOPLE-AI/handover/seminar/ (DATA-SARAH.md, compute_sarah.py). */

function MarketingCaseStudySarah() {
  /* The report is the site's lead magnet: everything a reader needs to judge
     the numbers is above the gate, and the working below it. */
  const { unlocked, unlock } = useCaseStudyAccess();

  return (
    <MarketingShell>
      <StudyHero
        eyebrow="People AI, Case study, September 2026"
        title="Sarah, an AI receptionist on two assisted-living phone lines"
        standfirst="Eight weeks of production use at Pine Lodge and Reunion Inn, Texas. What was deployed, how outcomes were measured, and what the records show."
        testId="text-case-study-sarah-title"
      />

      <StudyBody>
        <SarahStudySummary />

        {unlocked ? (
          <>
            <SarahStudyBackground />
            <SarahStudyDeployed />
            <SarahStudyMethod />
            <SarahStudyResults />
            <SarahStudyConclusions />
            <SarahStudyAppendix />
            <SarahStudyReferences />
          </>
        ) : (
          <CaseStudyGate
            study="Sarah, an AI receptionist on two assisted-living phone lines"
            slug="sarah-front-desk"
            promise="Eight weeks of production call records: the method, every figure with its definition, the transfer analysis, the receptionist-time model and its sensitivity table, and the monthly appendix."
            onUnlocked={unlock}
            className="my-12"
          />
        )}

        <StudyFooter>
          Prepared 8 September 2026. Figures are as read on that date. Staff are referred to by
          role; no caller or resident information appears in this document.
        </StudyFooter>
      </StudyBody>

      <MarketingCta
        title="Hear Sarah on your own line"
        subtitle="Book a demo, or run the live front-desk demos on this site first."
        secondaryLabel="Try it live"
        secondaryHref="/experience"
      />
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/case-studies/sarah-front-desk")({
  component: MarketingCaseStudySarah,
  head: () => {
    const seo = {
      title:
        "Case study: Sarah, an AI receptionist on two assisted-living phone lines | PEOPLE AI Workforce",
      description:
        "Eight weeks of production call records from two assisted-living communities in Texas: what was deployed, how coverage, transfers and messages were measured, and what the records show.",
      path: "/v2/case-studies/sarah-front-desk",
    };
    return marketingHead({
      ...seo,
      schema: [
        webPageOf(seo),
        breadcrumbs(
          { name: "Case studies", path: "/case-studies" },
          { name: "Sarah, front desk", path: "/case-studies/sarah-front-desk" },
        ),
      ],
    });
  },
});
